import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Lead {
  id: string;
  contact_name: string;
  lastContact: Date;
  estimated_value: number;
  status: string;
}

interface TopLeadsCardProps {
  leads: Lead[];
}

export const TopLeadsCard = ({ leads }: TopLeadsCardProps) => {
  const navigate = useNavigate();

  const getDaysAgo = (date: Date) => {
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    return days === 0 ? "Today" : `${days}d ago`;
  };

  const getUrgencyColor = (days: number) => {
    if (days > 7) return "bg-red-500/10 text-red-600 border-red-500/30";
    if (days > 3) return "bg-yellow-500/10 text-yellow-600 border-yellow-500/30";
    return "bg-green-500/10 text-green-600 border-green-500/30";
  };

  const topLeads = leads.slice(0, 3);

  if (topLeads.length === 0) {
    return (
      <Card className="bg-gradient-to-br from-neon-purple/20 to-neon-purple/5 border-neon-purple/30 rounded-3xl">
        <CardContent className="p-4">
          <h3 className="text-sm font-bold mb-2">🎯 Top Priority Leads</h3>
          <p className="text-xs text-muted-foreground py-2">
            No leads need immediate attention
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-neon-purple/20 to-neon-purple/5 border-neon-purple/30 rounded-3xl">
      <CardContent className="p-4">
        <h3 className="text-sm font-bold mb-3">🎯 Top Priority Leads</h3>
        <div className="space-y-2">
          {topLeads.map((lead) => {
            const daysAgo = Math.floor((Date.now() - lead.lastContact.getTime()) / (1000 * 60 * 60 * 24));
            return (
              <div
                key={lead.id}
                className="flex items-center justify-between p-2 bg-background/50 rounded-lg hover:bg-background/80 transition-colors cursor-pointer group"
                onClick={() => navigate(`/leads/${lead.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{lead.contact_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    <span className="text-[10px] text-muted-foreground">
                      {getDaysAgo(lead.lastContact)}
                    </span>
                    <Badge className={`text-[10px] px-1.5 py-0 ${getUrgencyColor(daysAgo)}`}>
                      ${(lead.estimated_value / 1000).toFixed(1)}K
                    </Badge>
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
